import { useNavigate, useLocation } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Heading, Text, useFeedback } from "@hawkins/components";
import { Typography } from "@hawkins/variables";

import { Page } from "../Page";
import { useContentfulTranslation } from "../../hooks/useContentfulTranslation";
import { ApiError, EmergencyForm, TranslationNamespace, UserInfoForm } from "../../types";
import EducateAccordion from "../EducateAccordion";
import { UserInfo, UserInfoRef } from "../forms/UserInfoForm";
import { useSession } from "../../hooks/useSessionContext";
import { EmergencyFormComponent, EmergencyFormRef } from "../forms/EmergencyForm";
import { useTemporaryRequest } from "../../hooks/useTemporaryTicket";
import { LoadingOverlay } from "../LoadingOverlay";
import { isApiError, sendApiRequest } from "../../services/sendApiRequest";
import { getEmergencyFromFormValues } from "../../utils";
import { prepareContentfulHtml } from '@lep-shared/richTextHtml';

enum CurrentPage {
  EDUCATE,
  USER_INFO,
  FORM,
}

export default function Emergency() {
  const { t, i18n } = useTranslation(TranslationNamespace.LEP);
  const { getContentfulEntry, isReady } = useContentfulTranslation();
  const userInfoRef = useRef<UserInfoRef>(null);
  const emergencyFormRef = useRef<EmergencyFormRef>(null);
  const [canSubmit, setCanSubmit] = useState(false);
  const { userInfo, setUserInfo } = useSession();
  const [currentPage, setCurrentPage] = useState<CurrentPage>(CurrentPage.EDUCATE);
  const { publicKey, requestId, setRequestId, setPublicKey } = useTemporaryRequest();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showError } = useFeedback();
  const navigate = useNavigate();
  const [disabled, setDisabled] = useState(false);
  const processedStateRef = useRef(false);
  const location = useLocation();

  const disclosureContent = getContentfulEntry("2FhxOiC4s8QjfyURnYlAtf");
  const educateContent = getContentfulEntry("7DUUDiBuoVeV7KuiLQnZX8");

  const onUserInfoSubmit = async (formValues: UserInfoForm) => {
    setIsSubmitting(true);
    setDisabled(true);

    setUserInfo(formValues);

    try {
      const response: any | ApiError = await sendApiRequest(
        '/temporary',
        {
          emailAddress: formValues.emailAddress,
          givenName: formValues.givenName,
          familyName: formValues.familyName,
        });
      if (isApiError(response)) {
        if (response.status === 400) {
          showError(t("invalid_domain_msg"));
        } else {
          showError(t("error_msg_2"));
        }
        return;
      }

      setRequestId(response.requestId);
      setPublicKey(response.publicKey);
      setCurrentPage(CurrentPage.FORM);
    } catch (error) {
      showError(t("error_msg_2"));
    } finally {
      setIsSubmitting(false);
      setDisabled(false);
    }
  };

  useEffect(() => {
    if (!processedStateRef.current && location.state && location.state.userInfo) {
      processedStateRef.current = true;
      onUserInfoSubmit(location.state.userInfo);
    }
  }, [location.state]);

  const onEmergencyFormSubmit = async (formValues: EmergencyForm) => {
    if (currentPage !== CurrentPage.FORM) {
      return;
    }

    setIsSubmitting(true);
    setDisabled(true);

    try {
      const emergencyForm = await getEmergencyFromFormValues(
        formValues,
        userInfo!,
        requestId!,
        publicKey!,
        i18n.language,
      );

      const response: any | ApiError = await sendApiRequest('/request', emergencyForm);
      if (isApiError(response)) {
        if (response.status === 400) {
          showError(t("invalid_domain_msg"));
        } else {
          showError(t("error_msg_2"));
        }
        return;
      }

      navigate("/request_submitted");
    } catch (error) {
      showError(t("error_msg_2"));
    } finally {
      setIsSubmitting(false);
      setDisabled(false);
    }
  };

  const handleUserInfoSubmit = () => {
    userInfoRef.current?.submit();
  };

  const handleEmergencyFormSubmit = () => {
    emergencyFormRef.current?.submit();
  };

  const handleUserInfoStateChange = (isValid: boolean, acceptTerms: boolean) => {
    setCanSubmit(isValid && acceptTerms);
  };

  const handleEmergencyFormStateChange = (isValid: boolean, signatureAgreement: boolean) => {
    setCanSubmit(isValid && signatureAgreement);
  };

  if (!isReady) {
    return <LoadingOverlay />;
  }

  if (!educateContent || !disclosureContent) {
    console.error("Contentful data not found for emergency page");
    return (
      <Page>
        <Text>{t('error_msg_2')}</Text>
      </Page>
    );
  }

  return (
    <LoadingOverlay
      isLoading={isSubmitting}
      size={60}
    >
      <Page
        renderFooterButton={
          () =>
          (<>
            {currentPage !== CurrentPage.EDUCATE ? (<Button
              onClick={() => {
                switch (currentPage) {
                  case CurrentPage.USER_INFO:
                    setCurrentPage(CurrentPage.EDUCATE);
                    break;
                  case CurrentPage.FORM:
                    setCurrentPage(CurrentPage.USER_INFO);
                    break;
                }
              }}
              variant="secondary"
            >
              {t("common_back")}
            </Button>) : null}
            <Button
              disabled={(!canSubmit && currentPage !== CurrentPage.EDUCATE) || disabled}
              onClick={() => {
                switch (currentPage) {
                  case CurrentPage.EDUCATE:
                    setCurrentPage(CurrentPage.USER_INFO);
                    break;
                  case CurrentPage.USER_INFO:
                    handleUserInfoSubmit();
                    break;
                  case CurrentPage.FORM:
                    handleEmergencyFormSubmit();
                    break;
                }
              }}
            >
              {currentPage === CurrentPage.FORM ? t("button_submit") : t("common_next_button")}
            </Button>
          </>)}
      >
        {currentPage === CurrentPage.EDUCATE ? (
          <>
            <Box>
              <Heading>{educateContent.title}</Heading>
              <div style={{ fontSize: Typography.FontSize16 }}
                dangerouslySetInnerHTML={{
                  __html: prepareContentfulHtml(educateContent.body),
                }}
              />
              <EducateAccordion title={disclosureContent.title} content={disclosureContent.body} />
            </Box>
          </>
        ) : null}
        {currentPage === CurrentPage.USER_INFO ? (
          <UserInfo
            onSubmit={(userInfo) => {
              if (currentPage === CurrentPage.USER_INFO) {
                onUserInfoSubmit(userInfo);
              }
            }}
            onFormStateChange={handleUserInfoStateChange}
            ref={userInfoRef}
          />
        ) : null}
        {currentPage === CurrentPage.FORM ? (
          <EmergencyFormComponent
            onSubmit={onEmergencyFormSubmit}
            onFormStateChange={handleEmergencyFormStateChange}
            ref={emergencyFormRef}
          />
        ) : null}
      </Page>
    </LoadingOverlay>
  );
};